import { Component, computed, inject, signal } from '@angular/core';
import { FormsModule } from '@angular/forms';
import {
	MAT_DIALOG_DATA,
	MatDialogActions,
	MatDialogClose,
	MatDialogContent,
	MatDialogRef,
	MatDialogTitle,
} from '@angular/material/dialog';
import { MatFormField } from '@angular/material/form-field';
import { MatInput } from '@angular/material/input';
import { TranslocoPipe, TranslocoService } from '@jsverse/transloco';
import { User } from '../../../api/app-types/user.app';
import { USER_SERVICE, UserApi } from '../../../api/services/user.service';
import { LvrField } from '../../../core/components/field/field';
import { LvrInputDirective } from '../../../core/directives/lvr-input.directive';
import { NOTIFICATION_SERVICE } from '../../../shared/notification/notification';
import { LvrButton } from '../../../core/components/lvr-button/lvr-button';
import { createErrorNotification, createSuccessNotification } from '../../../shared/notification/notification.factory';

@Component({
	selector: 'lvr-user-deactivate-dialog',
	template: `
		<h2 mat-dialog-title>{{ 'user.deactivate.title' | transloco }}</h2>
		<mat-dialog-content>
			<p>
				{{ 'user.deactivate.confirmation' | transloco: { firstName: user.firstName, lastName: user.lastName } }}
			</p>
			<lvr-field [label]="'user.deactivate.doublecheck' | transloco">
				<mat-form-field lvrInput class="full-width">
					<input
						matInput
						[(ngModel)]="confirmationInput"
						[placeholder]="'user.deactivate.insert_name' | transloco: { lastName: user.lastName }"
					/>
				</mat-form-field>
			</lvr-field>
		</mat-dialog-content>
		<mat-dialog-actions class="actions-row">
			<lvr-button
				variant="secondary"
				[label]="'user.deactivate.cancel' | transloco"
				[type]="'button'"
				[disabled]="loading()"
				icon="disabled_by_default"
				mat-dialog-close
			></lvr-button>
			<lvr-button
				variant="primary"
				[label]="'user.deactivate.deactivate' | transloco"
				[type]="'button'"
				[disabled]="!isDeactivateEnabled() || loading()"
				[loading]="loading()"
				[loadingLabel]="'user.deactivate.deactivating' | transloco"
				(clicked)="deactivateUser()"
				icon="block"
			></lvr-button>
		</mat-dialog-actions>
	`,
	styles: [
		`
			.full-width {
				width: 100%;
				margin-top: 16px;
			}
		`,
	],
	imports: [
		MatDialogTitle,
		MatDialogContent,
		MatDialogActions,
		MatDialogClose,
		MatFormField,
		MatInput,
		FormsModule,
		LvrInputDirective,
		LvrField,
		TranslocoPipe,
		LvrButton,
	],
	providers: [{ provide: USER_SERVICE, useClass: UserApi }],
})
export class UserDeactivateDialog {
	public readonly user: User = inject(MAT_DIALOG_DATA);
	private readonly dialogRef = inject(MatDialogRef<UserDeactivateDialog>);
	private readonly userService = inject(USER_SERVICE);
	private readonly ntfySrv = inject(NOTIFICATION_SERVICE);
	private readonly transloco = inject(TranslocoService);

	public readonly confirmationInput = signal('');
	public readonly loading = signal(false);

	public readonly isDeactivateEnabled = computed(() => {
		return this.confirmationInput().trim() === this.user.lastName.trim();
	});

	public async deactivateUser() {
		if (this.loading() || !this.isDeactivateEnabled()) return;

		this.loading.set(true);

		try {
			await this.userService.deactivateUser(this.user.objectId);

			this.ntfySrv.addNotification(
				createSuccessNotification(
					this.transloco.translate('base.notification_message.user_deactivate_success', {
						firstName: this.user.firstName,
						lastName: this.user.lastName,
					}),
				),
			);
			this.dialogRef.close(true);
			// eslint-disable-next-line @typescript-eslint/no-unused-vars
		} catch (_: unknown) {
			this.ntfySrv.addNotification(
				createErrorNotification(this.transloco.translate('base.notification_message.user_deactivate_error')),
			);
		} finally {
			this.loading.set(false);
		}
	}
}
